import * as Sentry from '@sentry/react'
import { SENTRY_IGNORE_ERRORS, SENTRY_DENY_URLS } from '@messenger/consts'
import i18n from 'i18next'
import {
  scrubReplayAndFeedbackIntegration,
  scrubReplayFrameEvent,
  scrubSentryEvent,
} from 'utils/global/scrubTrackedUrl'

/**
 * Every Sentry event, trace and replay leaves with the edit link's `token` and
 * `access` values scrubbed (`scrubTrackedUrl`); the app still reads the real
 * URL. Exported so a test boots a real client with exactly these options.
 */
export const SENTRY_SCRUB_OPTIONS = {
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
}

/** Session replay masks every input and scrubs the URLs its frames record. */
export const SENTRY_REPLAY_OPTIONS = {
  maskAllInputs: true,
  beforeAddRecordingEvent: scrubReplayFrameEvent,
}

export function initSentryWithCurrentLanguage(
  options: { widgetOnly?: boolean } = {},
) {
  const feedbackIntegration = Sentry.feedbackIntegration({
    colorScheme: 'system',
    isNameRequired: true,
    showBranding: false,
    isEmailRequired: true,
    autoInject: false,
    triggerLabel: i18n.t('sentry.triggerLabel'),
    formTitle: i18n.t('sentry.formTitle'),
    submitButtonLabel: i18n.t('sentry.submitButtonLabel'),
    cancelButtonLabel: i18n.t('sentry.cancelButtonLabel'),
    nameLabel: i18n.t('sentry.nameLabel'),
    namePlaceholder: i18n.t('sentry.namePlaceholder'),
    emailLabel: i18n.t('sentry.emailLabel'),
    emailPlaceholder: i18n.t('sentry.emailPlaceholder'),
    messageLabel: i18n.t('sentry.messageLabel'),
    messagePlaceholder: i18n.t('sentry.messagePlaceholder'),
    addScreenshotButtonLabel: i18n.t('sentry.addScreenshotButtonLabel'),
    successMessageText: i18n.t('sentry.successMessageText'),
    isRequiredLabel: false,
    translations: {
      en: {
        triggerLabel: i18n.getFixedT('en')('sentry.triggerLabel'),
        formTitle: i18n.getFixedT('en')('sentry.formTitle'),
        submitButtonLabel: i18n.getFixedT('en')('sentry.submitButtonLabel'),
        cancelButtonLabel: i18n.getFixedT('en')('sentry.cancelButtonLabel'),
        nameLabel: i18n.getFixedT('en')('sentry.nameLabel'),
        namePlaceholder: i18n.getFixedT('en')('sentry.namePlaceholder'),
        emailLabel: i18n.getFixedT('en')('sentry.emailLabel'),
        emailPlaceholder: i18n.getFixedT('en')('sentry.emailPlaceholder'),
        messageLabel: i18n.getFixedT('en')('sentry.messageLabel'),
        messagePlaceholder: i18n.getFixedT('en')('sentry.messagePlaceholder'),
        addScreenshotButtonLabel: i18n.getFixedT('en')(
          'sentry.addScreenshotButtonLabel',
        ),
        successMessageText: i18n.getFixedT('en')('sentry.successMessageText'),
      },
      es: {
        triggerLabel: i18n.getFixedT('es')('sentry.triggerLabel'),
        formTitle: i18n.getFixedT('es')('sentry.formTitle'),
        submitButtonLabel: i18n.getFixedT('es')('sentry.submitButtonLabel'),
        cancelButtonLabel: i18n.getFixedT('es')('sentry.cancelButtonLabel'),
        nameLabel: i18n.getFixedT('es')('sentry.nameLabel'),
        namePlaceholder: i18n.getFixedT('es')('sentry.namePlaceholder'),
        emailLabel: i18n.getFixedT('es')('sentry.emailLabel'),
        emailPlaceholder: i18n.getFixedT('es')('sentry.emailPlaceholder'),
        messageLabel: i18n.getFixedT('es')('sentry.messageLabel'),
        messagePlaceholder: i18n.getFixedT('es')('sentry.messagePlaceholder'),
        addScreenshotButtonLabel: i18n.getFixedT('es')(
          'sentry.addScreenshotButtonLabel',
        ),
        successMessageText: i18n.getFixedT('es')('sentry.successMessageText'),
      },
      fr: {
        triggerLabel: i18n.getFixedT('fr')('sentry.triggerLabel'),
        formTitle: i18n.getFixedT('fr')('sentry.formTitle'),
        submitButtonLabel: i18n.getFixedT('fr')('sentry.submitButtonLabel'),
        cancelButtonLabel: i18n.getFixedT('fr')('sentry.cancelButtonLabel'),
        nameLabel: i18n.getFixedT('fr')('sentry.nameLabel'),
        namePlaceholder: i18n.getFixedT('fr')('sentry.namePlaceholder'),
        emailLabel: i18n.getFixedT('fr')('sentry.emailLabel'),
        emailPlaceholder: i18n.getFixedT('fr')('sentry.emailPlaceholder'),
        messageLabel: i18n.getFixedT('fr')('sentry.messageLabel'),
        messagePlaceholder: i18n.getFixedT('fr')('sentry.messagePlaceholder'),
        addScreenshotButtonLabel: i18n.getFixedT('fr')(
          'sentry.addScreenshotButtonLabel',
        ),
        successMessageText: i18n.getFixedT('fr')('sentry.successMessageText'),
      },
    },
  })

  Sentry.init({
    enabled: true,
    dsn: 'https://d5e4237a63e7dbd4a53505592d632ee4@o4507587149234176.ingest.us.sentry.io/4509197436059648',
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    denyUrls: SENTRY_DENY_URLS,
    ...SENTRY_SCRUB_OPTIONS,
    integrations: options.widgetOnly
      ? [feedbackIntegration, scrubReplayAndFeedbackIntegration()]
      : [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(SENTRY_REPLAY_OPTIONS),
          feedbackIntegration,
          scrubReplayAndFeedbackIntegration(),
        ],
    ...(options.widgetOnly
      ? {}
      : {
          // Performance Monitoring
          /**
           * Share of page loads and navigations that send a performance trace.
           * A traced page load sends a span for every fetch, script and asset,
           * and all HQ projects share one monthly Sentry span quota.
           */
          tracesSampleRate: 0.1,
          tracePropagationTargets: [
            'localhost',
            'https://mydesignerlab.com/',
            'https://staging.mydesignerlab.com/',
            'https://my-designer-lab-dev.vercel.app/',
            'https://my-designer-lab-dev-2.vercel.app/',
          ],
          // Session Replay
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        }),
  })
}

export function recreateSentryFeedbackWidget() {
  const feedback = Sentry.getFeedback()
  if (feedback) {
    const widget = feedback.createWidget()
    widget.appendToDom()
    return widget
  }
  return null
}
